import { Button, Card, Form, InputNumber, Space, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Settings = {
  id: string | null;
  max_open_loans: number;
  default_loan_days: number;
  max_renewals: number;
  max_open_reservations: number;
  ready_hold_days: number;
  record_version: number;
};

export default function LibrarySettingsPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.library.manage') ||
      user?.permissions?.includes('education.admin')
  );

  const settings = useQuery({
    queryKey: ['education-library-settings'],
    queryFn: async () => {
      const res = await api.get<Settings>('/education/library/settings');
      return res.data;
    },
  });

  useEffect(() => {
    if (settings.data) form.setFieldsValue(settings.data);
  }, [settings.data, form]);

  const save = useMutation({
    mutationFn: (values: Settings) =>
      api.put('/education/library/settings', {
        ...values,
        record_version: settings.data?.record_version ?? 0,
      }),
    onSuccess: () => {
      message.success(t('librarySettingsSaved'));
      queryClient.invalidateQueries({ queryKey: ['education-library-settings'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={t('librarySettings')}
      description={t('librarySettingsIntro')}
      extra={<Button onClick={() => navigate('/education/library')}>{t('backToLibrary')}</Button>}
    >
      <Card size="small" loading={settings.isLoading}>
        <Form
          form={form}
          layout="vertical"
          disabled={!canManage}
          onFinish={(values) => save.mutate(values as Settings)}
          style={{ maxWidth: 480 }}
        >
          <Form.Item
            name="max_open_loans"
            label={t('maxOpenLoans')}
            rules={[{ required: true }]}
          >
            <InputNumber min={1} max={50} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="default_loan_days"
            label={t('defaultLoanDays')}
            rules={[{ required: true }]}
          >
            <InputNumber min={1} max={90} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="max_renewals" label={t('maxRenewals')} rules={[{ required: true }]}>
            <InputNumber min={0} max={20} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="max_open_reservations"
            label={t('maxOpenReservations')}
            rules={[{ required: true }]}
          >
            <InputNumber min={0} max={50} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="ready_hold_days" label={t('readyHoldDays')} rules={[{ required: true }]}>
            <InputNumber min={1} max={30} style={{ width: '100%' }} />
          </Form.Item>
          {canManage ? (
            <Space>
              <Button type="primary" htmlType="submit" loading={save.isPending}>
                {t('save')}
              </Button>
            </Space>
          ) : null}
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
